import { PaginationModel } from '../../utils/pagination';
import {
  Action,
  getModule,
  Module,
  Mutation,
  VuexModule,
} from 'vuex-module-decorators';
import store from '@/store';
import { ErrorResponse } from '@/utils/response';
import { axios, CustomAxiosResponse } from '@/utils/axios';
import {
  PreCollection,
  PreCollectionSortBy,
  PreCollectionType,
} from './pre-collection.model';
import { SubjectType } from '../subject/subject.model';
import { objectKeyCamelToSnakeCase } from '@/utils/common/snake-case-converter';

export interface PreCollectionStoreInterface {
  preCollection: PreCollection | null;
  preCollections: PreCollection[];
  error: ErrorResponse | null;
  pagination: PaginationModel;
  fetchPreCollection(uuid: string): Promise<void>;
  fetchPreCollections(params: PreCollectionsParams): Promise<void>;
}

export interface PreCollectionsParams {
  subjectUuid: string;
  keyword: string;
  subjectType: SubjectType;
  sortBy: PreCollectionSortBy;
  preCollectionTypes: PreCollectionType[];
  years: number[];
  levels: number[];
}

@Module({
  namespaced: true,
  dynamic: true,
  name: 'pre-collection',
  store,
})
class PreCollectionStore extends VuexModule
  implements PreCollectionStoreInterface {
  preCollection: PreCollection | null = null;
  preCollections: PreCollection[] = [];
  error: ErrorResponse | null = null;
  pagination = { ...this.defaultPagination };

  get defaultPagination(): PaginationModel {
    return {
      page: 1,
      limit: 6,
      totalPages: 1,
      totalRecords: 1,
      isResetPage: false,
    };
  }

  @Mutation
  updatePreCollection(params: PreCollection) {
    this.preCollection = params;
  }

  @Mutation
  updatePreCollections(params: PreCollection[]) {
    if (this.preCollections.length === 0 || this.pagination.isResetPage) {
      this.preCollections = params;
      this.pagination.isResetPage = false;
    } else {
      this.preCollections.push(...params);
    }
  }

  @Mutation
  setError(params: ErrorResponse) {
    this.error = params;
  }

  @Mutation
  updatePagination(params: PaginationModel) {
    this.pagination = params;
  }

  @Action
  async fetchPreCollection(uuid: string): Promise<void> {
    this.context.commit('updatePreCollection', null);
    this.context.commit('setError', null);

    try {
      const { data }: CustomAxiosResponse = await axios.get(
        `/pre-collections/${uuid}`,
      );

      this.context.commit('updatePreCollection', data);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }

  @Action
  async fetchPreCollections(params: PreCollectionsParams): Promise<void> {
    this.context.commit('updatePreCollections', []);
    this.context.commit('setError', null);

    const convertedParams = objectKeyCamelToSnakeCase(params);

    try {
      const { data, pagination }: CustomAxiosResponse = await axios.get(
        `/pre-collections`,
        {
          params: {
            ...convertedParams,
            page: this.pagination.page,
            limit: this.pagination.limit,
          },
        },
      );

      this.context.commit('updatePreCollections', data);
      this.context.commit('updatePagination', pagination);
    } catch (error) {
      this.context.commit('setError', error);
    }
  }
}

export default getModule(PreCollectionStore);
